/*
 * Copyright (C) 2023 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MESSAGES_DATA_SERVICE, MessagesDataService } from '../../page-modules/messages/services/messages-data.service';
import { REVIEWS_DATA_SERVICE, ReviewsDataService } from '../../page-modules/reviews/services/data.service';
import { Notification } from '../../shared/models/notifications/notifications.model';
import { V3BookingDataService, V3_BOOKING_DATA_SERVICE } from '../../shared/services/bookings/v3-booking-data.service';
import { NOTIFICATIONS_DATA_SERVICE, NotificationsDataService } from '../../shared/services/notifications/notifications-data.service';
import * as HeaderCountsActions from './header-counts.action';

const UPCOMING_BOOKINGS_WINDOW_DAYS = 180;
// The nav badge only needs a count of what is imminent, so it reads one page rather than the whole window.
const BOOKINGS_BADGE_PAGE_SIZE = 100;

export const INITIAL_HEADER_COUNTS_STATE: HeaderCountsStateModel = {
  unreadNotificationsCount: 0,
  unreadMessagesCount: 0,
  pendingReviewsCount: 0,
  upcomingBookingsCount: 0,
  bookingsNeedAttention: false,
};

export interface HeaderCountsStateModel {
  unreadNotificationsCount: number;
  unreadMessagesCount: number;
  pendingReviewsCount: number;
  upcomingBookingsCount: number;
  bookingsNeedAttention: boolean;
}

@State<HeaderCountsStateModel>({
  name: 'headerCounts',
  defaults: INITIAL_HEADER_COUNTS_STATE,
})
@Injectable()
export class HeaderCountsState {
  @Selector()
  static unreadNotificationsCount({ unreadNotificationsCount }: HeaderCountsStateModel) {
    return unreadNotificationsCount;
  }

  @Selector()
  static unreadMessagesCount({ unreadMessagesCount }: HeaderCountsStateModel) {
    return unreadMessagesCount;
  }
  @Selector()
  static pendingReviewsCount({ pendingReviewsCount }: HeaderCountsStateModel) {
    return pendingReviewsCount;
  }

  @Selector()
  static upcomingBookingsCount({ upcomingBookingsCount }: HeaderCountsStateModel) {
    return upcomingBookingsCount;
  }

  @Selector()
  static bookingsNeedAttention({ bookingsNeedAttention }: HeaderCountsStateModel) {
    return bookingsNeedAttention;
  }

  constructor(
    @Inject(MESSAGES_DATA_SERVICE) private messagesDataService: MessagesDataService,
    @Inject(REVIEWS_DATA_SERVICE) private reviewsDataService: ReviewsDataService,
    @Inject(NOTIFICATIONS_DATA_SERVICE) private notificationsDataService: NotificationsDataService,
    @Inject(V3_BOOKING_DATA_SERVICE) private bookingDataService: V3BookingDataService,
  ) {}

  @Action(HeaderCountsActions.UpdateNotificationsCount)
  updateNotificationsCount({ patchState }: StateContext<HeaderCountsStateModel>) {
    return this.notificationsDataService.loadNotificationsSnapshot().subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        const unreadNotifications = value.reduce((count: number, notification: Notification) => (notification.read ? count : count + 1), 0);
        patchState({
          unreadNotificationsCount: unreadNotifications,
        });
      }
    });
  }

  @Action(HeaderCountsActions.UpdateMessagesCount)
  updateMessagesCount({ patchState }: StateContext<HeaderCountsStateModel>) {
    return this.messagesDataService.loadReceivedMessages(0, 10, false).subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        const messagesCount = value.content.length;
        patchState({
          unreadMessagesCount: messagesCount,
        });
      }
    });
  }

  @Action(HeaderCountsActions.UpdateReviewsCount)
  updateReviewsCount({ patchState }: StateContext<HeaderCountsStateModel>) {
    return this.reviewsDataService.getUserReviewsCount().subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        const reviewsCount = value.pendingCount;
        patchState({
          pendingReviewsCount: reviewsCount,
        });
      }
    });
  }

  @Action(HeaderCountsActions.UpdateUpcomingBookingsCount)
  updateUpcomingBookingsCount({ patchState }: StateContext<HeaderCountsStateModel>) {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + UPCOMING_BOOKINGS_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
    return this.bookingDataService
      .getMyBookings(from, to, { size: BOOKINGS_BADGE_PAGE_SIZE, ascending: true })
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          const bookings = value?.content ?? [];
          patchState({
            upcomingBookingsCount: bookings.filter(
              (booking) => booking.status === 'PENDING' || booking.status === 'TIME_SUGGESTED' || booking.status === 'ACCEPTED',
            ).length,
            bookingsNeedAttention: bookings.some((booking) => booking.status === 'TIME_SUGGESTED' && booking.awaiting === 'LEARNER'),
          });
        }
      });
  }
}
